/**
 * AI Logging Settings Component
 * TASK-335: AI Assistant Configuration & Restrictions
 *
 * Logging toggle and retention settings.
 */

import React from 'react';
import { FileText, Clock, Shield, Eye } from 'lucide-react';
import type { AISettingsUpdateData } from '../../../types/admin';
import { aiSettingsService } from '../../../services/admin';

interface AILoggingSettingsProps {
  formData: AISettingsUpdateData;
  onChange: (updates: Partial<AISettingsUpdateData>) => void;
}

const RETENTION_PRESETS = [
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
  { days: 365, label: '1 year' },
  { days: 730, label: '2 years' },
  { days: 2555, label: '7 years (Swiss compliance)' },
];

export const AILoggingSettings: React.FC<AILoggingSettingsProps> = ({ formData, onChange }) => {
  const loggingEnabled = formData.log_ai_interactions ?? true;
  const retentionDays = formData.log_retention_days ?? 90;
  const anonymizeLogs = formData.anonymize_logs ?? true;
  const logComplianceFlags = formData.log_compliance_flags ?? true;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Interaction Logging</h3>
        <p className="text-sm text-gray-500 mt-1">
          Configure how AI interactions are logged for audit and compliance purposes.
        </p>
      </div>

      {/* Master Toggle */}
      <div
        className={`
          p-4 rounded-lg border-2 transition-colors
          ${loggingEnabled ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'}
        `}
      >
        <label className="flex items-center justify-between cursor-pointer">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${loggingEnabled ? 'bg-green-100' : 'bg-gray-200'}`}>
              <FileText size={20} className={loggingEnabled ? 'text-green-600' : 'text-gray-400'} />
            </div>
            <div>
              <span className="font-medium text-gray-900">Log AI Interactions</span>
              <p className="text-sm text-gray-500">
                {loggingEnabled
                  ? 'Interactions are being logged for audit'
                  : 'No interaction logging (not recommended)'}
              </p>
            </div>
          </div>
          <div className="relative">
            <input
              type="checkbox"
              checked={loggingEnabled}
              onChange={(e) => onChange({ log_ai_interactions: e.target.checked })}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
          </div>
        </label>
      </div>

      {loggingEnabled && (
        <>
          {/* Log Options */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-gray-700">Log Options</h4>

            <label className="flex items-center justify-between p-4 rounded-lg border border-gray-200 cursor-pointer hover:border-gray-300">
              <div className="flex items-center gap-3">
                <Eye size={18} className={anonymizeLogs ? 'text-green-600' : 'text-gray-400'} />
                <div>
                  <span className="font-medium text-gray-900">Anonymize Logs</span>
                  <p className="text-sm text-gray-500">
                    Store anonymized summaries instead of raw prompts/responses (GDPR recommended)
                  </p>
                </div>
              </div>
              <input
                type="checkbox"
                checked={anonymizeLogs}
                onChange={(e) => onChange({ anonymize_logs: e.target.checked })}
                className="rounded border-gray-300 text-green-600 focus:ring-green-500"
              />
            </label>

            <label className="flex items-center justify-between p-4 rounded-lg border border-gray-200 cursor-pointer hover:border-gray-300">
              <div className="flex items-center gap-3">
                <Shield
                  size={18}
                  className={logComplianceFlags ? 'text-blue-600' : 'text-gray-400'}
                />
                <div>
                  <span className="font-medium text-gray-900">Log Compliance Flags</span>
                  <p className="text-sm text-gray-500">
                    Track when PII is detected, guardrails trigger, or restrictions are hit
                  </p>
                </div>
              </div>
              <input
                type="checkbox"
                checked={logComplianceFlags}
                onChange={(e) => onChange({ log_compliance_flags: e.target.checked })}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
            </label>
          </div>

          {/* Retention Period */}
          <div className="pt-4 border-t border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Clock size={20} className="text-amber-600" />
              </div>
              <div>
                <h4 className="font-medium text-gray-900">Retention Period</h4>
                <p className="text-sm text-gray-500">
                  How long to keep interaction logs before automatic deletion
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <input
                  type="range"
                  min={30}
                  max={2555}
                  value={retentionDays}
                  onChange={(e) => onChange({ log_retention_days: parseInt(e.target.value, 10) })}
                  className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#002B45]"
                />
                <div className="text-sm font-medium text-gray-900 min-w-[100px] text-right">
                  {aiSettingsService.formatRetentionDays(retentionDays)}
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                {RETENTION_PRESETS.map((preset) => (
                  <button
                    key={preset.days}
                    onClick={() => onChange({ log_retention_days: preset.days })}
                    className={`
                      px-3 py-1.5 text-sm border rounded-lg transition-colors
                      ${retentionDays === preset.days
                        ? 'bg-[#002B45] text-white border-[#002B45]'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
                      }
                    `}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Info Box */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="text-sm text-blue-800">
          <p className="font-medium">GDPR & Swiss Compliance Notes</p>
          <ul className="mt-1 list-disc list-inside text-xs">
            <li>GDPR recommends data minimization - only log what's necessary</li>
            <li>Swiss banking regulations may require 7+ years retention for audit trails</li>
            <li>Anonymized logs help balance compliance with privacy</li>
            <li>Log checksums ensure tamper-evident audit trails</li>
          </ul>
        </div>
      </div>

      {!loggingEnabled && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm text-amber-800">
            <strong>Warning:</strong> Disabling logging removes audit capabilities and may violate
            compliance requirements. Consider enabling anonymized logging as a minimum.
          </p>
        </div>
      )}
    </div>
  );
};

export default AILoggingSettings;
